<template>
  <div class="wrapper">
    <div class="canvases">
      <canvas
        ref="speaker1"
        class="canvas"
        height="0"
        @click="playAudio"
      />
      <canvas
        ref="speaker2"
        class="canvas"
        height="0"
        @click="playAudio"
      />
    </div>
    <div
      ref="progress"
      class="progress"
    />
    <div
      ref="cursor"
      class="cursor"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, DeepReadonly } from 'vue';
import { Transcription } from '@/api/openapi';
import { useTranscriptionSpeakers, SPEAKER_COLORS } from '@/composable/user/transcription/transcriptionSpeakers';

const props = defineProps<TProps>();
const emit = defineEmits<TEmits>();
type TProps = {
  transcription: DeepReadonly<Transcription>;
  audioUrl: string;
  audioRef: HTMLAudioElement;
  canvasWidth: number;
  canvasHeight: number;
};
type TEmits = {
  'click:playAt': [time: number];
}

const { findInsideSalesSpeaker, findCustomerSpeaker } = useTranscriptionSpeakers(() => props.transcription);

const speaker1 = ref<HTMLCanvasElement>();
const speaker2 = ref<HTMLCanvasElement>();
const cursor = ref<HTMLElement>();
const progress = ref<HTMLElement>();
const duration = ref(0); // 音声の時間
const pxPerSec = ref(0); // 1秒辺りのpx数
const barSize = 2; // 棒の太さ
const barGap = 1; // 棒のスペース

onMounted(async () => {
  // 音声データをAudioBuffer形式で取得
  const audioBuffer = await loadAudioBuffer();
  // 音声データを描画用に丸める
  const mergedPeaks = getShapeData(audioBuffer);
  // 音声波形を描画
  drawCanvas(mergedPeaks, speaker1, SPEAKER_COLORS.insideSales, findInsideSalesSpeaker());
  drawCanvas(mergedPeaks, speaker2, SPEAKER_COLORS.customer, findCustomerSpeaker());
  // 音声再生時のプログレス描画
  props.audioRef.addEventListener('timeupdate', progressUpdate);
});

onBeforeUnmount(async () => {
  props.audioRef.removeEventListener('timeupdate', progressUpdate);
});

const loadAudioBuffer = async () => {
  const audioCtx = new AudioContext();
  const arrayBuffer = await fetch(props.audioUrl).then((response) => response.arrayBuffer());
  const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
  return audioBuffer;
};

const getShapeData = (audioBuffer) => {
  // 描画サイズを算出
  duration.value = audioBuffer.duration;
  pxPerSec.value = props.canvasWidth / audioBuffer.duration;
  const splitLength = Math.ceil(props.canvasWidth / (barSize + barGap));
  // 音声データを描画サイズで丸める
  const channels = audioBuffer.numberOfChannels;
  const splitPeaks = [];
  const sampleSize = audioBuffer.length / splitLength;
  const sampleStep = Math.floor(sampleSize / 10) || 1;
  for (let c = 0; c < channels; c++) {
    splitPeaks[c] = new Array(splitLength);
    const peaks = splitPeaks[c];
    const channelData = audioBuffer.getChannelData(c);
    for (let i = 0; i < splitLength; i++) {
      const start = Math.floor(i * sampleSize);
      const end = Math.floor(start + sampleSize);
      let max = 0;
      for (let j = start; j < end; j += sampleStep) {
        const value = Math.abs(channelData[j]);
        max = Math.max(max, value);
      }
      peaks[i] = Math.round(max * 100) / 100;
    }
  }
  const mergedPeaks = new Array(splitLength);
  for (let i = 0; i < splitLength; i++) {
    let max = 0;
    for (let c = 0; c < channels; c++) {
      const peaks = splitPeaks[c];
      const value = peaks[i];
      max = Math.max(max, value);
    }
    mergedPeaks[i] = max;
  }
  return mergedPeaks;
};

const drawCanvas = (shapeData, speakerRef, color, speakerKey: string) => {
  if (!speakerRef.value) return;

  // 音声波形を描画
  const ctx = speakerRef.value.getContext('2d');
  speakerRef.value.width = props.canvasWidth;
  speakerRef.value.height = props.canvasHeight;
  ctx.clearRect(0, 0, props.canvasWidth, props.canvasHeight);
  ctx.fillStyle = color;

  const length = shapeData.length;
  const halfHeight = props.canvasHeight / 2;
  for (let i = 0; i < length; i++) {
    const x = (barSize + barGap) * i;
    const sec = x / pxPerSec.value;

    let hit = false;
    props.transcription?.mergedTexts.forEach(segment => {
      if (segment.speaker === speakerKey && segment.start <= sec && sec <= segment.end) {
        hit = true;
        return false;
      }
    });
    const data = hit && shapeData[i] > 0.1 ? shapeData[i] : 0.1;
    const waveHeight = props.canvasHeight * data;
    const halfWaveHeight = waveHeight / 2;
    const y = halfHeight - halfWaveHeight;
    ctx.fillRect(x, y, barSize, waveHeight);
  }
};

const playAudio = (event) => {
  // クリック位置から再生位置を算出
  const time = event.offsetX / pxPerSec.value;
  // 位置指定再生
  emit('click:playAt', time);
};

const progressUpdate = () => {
  const percents = props.audioRef.currentTime / duration.value * 100;
  if (cursor.value) {
    cursor.value.style.left = `${percents}%`;
    cursor.value.style.transform = `translateX(-${Math.round(percents) === 100 ? 1.5 : 0}px)`;
  }
  if (progress.value) {
    progress.value.style.width = `${percents}%`;
  }
};
</script>

<style lang="scss" scoped>
.wrapper {
  position: relative;
  overflow: visible;
  z-index: 2;
}
.canvases > div {
  position: relative;
}
.canvas {
  display: block;
  image-rendering: pixelated;
  margin-bottom: $basespace-400;
  cursor: pointer;
}
.cursor {
  pointer-events: none;
  position: absolute;
  z-index: 5;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 2px;
  background-color: $bdcolor-light;
  width: 1.5px;
}
.progress {
  pointer-events: none;
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 0;
  height: 100%;
  overflow: hidden;
  background-color: $concrete-dark;
  opacity: 0.2;
}
</style>
